import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Trash2, Mail, Instagram, ChevronRight, ArrowLeft, Download } from "lucide-react";

const STATUSES = [
  { v: "new", label: "New" },
  { v: "contacted", label: "Contacted" },
  { v: "scheduled", label: "Call booked" },
  { v: "qualified", label: "Qualified" },
  { v: "enrolled", label: "Enrolled" },
  { v: "archived", label: "Archived" },
];

const PROGRAM_LABEL = {
  mtm: "Metabolic Transformation Method",
  rise_within: "Rise Within (1:1)",
  not_sure: "Help me decide",
};

export default function CoachLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(() => api.get("/leads", { params: filter === "all" ? {} : { status: filter } })
    .then((r) => setLeads(r.data)).catch(() => {}), [filter]);
  useEffect(() => { load(); }, [load]);

  const open = async (l) => { setActive(l); setNotes(l.notes || ""); };

  const updateStatus = async (status) => {
    try {
      const { data } = await api.patch(`/leads/${active.lead_id}`, { status });
      setActive(data);
      toast.success("Status updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveNotes = async () => {
    try {
      const { data } = await api.patch(`/leads/${active.lead_id}`, { notes });
      setActive(data);
      toast.success("Notes saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    await api.delete(`/leads/${active.lead_id}`);
    setActive(null);
    load();
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/leads-export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "wellness-way-leads.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch { toast.error("Export failed"); }
  };

  if (active) {
    return (
      <div className="space-y-8" data-testid="lead-detail">
        <button onClick={() => setActive(null)} className="btn-ghost" data-testid="lead-back">
          <ArrowLeft size={12} /> Back to inbox
        </button>
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="overline">Application</p>
            <h1 className="font-display text-4xl mt-4">{active.name}</h1>
            <div className="font-soft text-base mt-2" style={{ color: "var(--text-secondary)" }}>
              <Mail size={12} className="inline mr-2" />{active.email}
              {active.instagram && <><span className="mx-3">·</span><Instagram size={12} className="inline mr-2" />{active.instagram}</>}
              {active.age && <><span className="mx-3">·</span>Age {active.age}</>}
            </div>
            <div className="bronze-rule mt-5" />
          </div>
          <button onClick={remove} className="btn-ghost" data-testid="lead-delete"><Trash2 size={14}/> Delete</button>
        </header>

        {/* Status pills */}
        <section className="card-luxe">
          <p className="overline">Status</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button key={s.v} onClick={() => updateStatus(s.v)} data-testid={`lead-status-${s.v}`}
                className="px-4 py-2 font-label text-[10px] tracking-[0.2em] uppercase border transition-colors"
                style={{
                  borderColor: active.status === s.v ? "var(--accent)" : "var(--border-strong)",
                  background: active.status === s.v ? "var(--accent)" : "transparent",
                  color: active.status === s.v ? "#FFFFFF" : "var(--text-secondary)",
                }}>{s.label}</button>
            ))}
          </div>
        </section>

        {/* Quick facts grid */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: "var(--border-strong)" }}>
          <div className="p-6" style={{ background: "var(--surface-1)" }}>
            <p className="overline">Program</p>
            <div className="font-display text-base mt-3 leading-tight">{PROGRAM_LABEL[active.program_interest] || "—"}</div>
          </div>
          <div className="p-6" style={{ background: "var(--surface-1)" }}>
            <p className="overline">Commitment</p>
            <div className="font-display text-2xl mt-3">{active.commitment_level || "—"}<span className="text-base" style={{ color: "var(--text-tertiary)" }}>/10</span></div>
          </div>
          <div className="p-6" style={{ background: "var(--surface-1)" }}>
            <p className="overline">Timeline</p>
            <div className="font-soft text-lg mt-3">{active.timeline || "—"}</div>
          </div>
        </section>

        {/* Goals */}
        {active.goals?.length > 0 && (
          <section className="card-luxe">
            <p className="overline">Goals</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {active.goals.map((g) => (
                <span key={g} className="px-3 py-1.5 font-label text-[10px] tracking-[0.16em] uppercase border" style={{ borderColor: "var(--accent)", color: "var(--accent)" }}>{g}</span>
              ))}
            </div>
          </section>
        )}

        {/* Long answers */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { label: "Top goal · Erase 3 problems", value: active.top_goal },
            { label: "Past diets", value: active.past_diets },
            { label: "Current exercise", value: active.current_exercise },
            { label: "Hormones", value: active.hormone_details, sub: active.hormone_status },
            { label: "Biggest hurdle", value: active.biggest_hurdle },
            { label: "Missed experiences", value: active.missed_experiences },
            { label: "Her WHY", value: active.why, featured: true },
            { label: "Looking for in a coach", value: active.looking_for },
          ].filter((b) => b.value).map((b) => (
            <div key={b.label} className="card-luxe" style={{ borderColor: b.featured ? "var(--accent)" : "var(--border-strong)" }}>
              <p className="overline">{b.label}{b.sub ? ` · ${b.sub}` : ""}</p>
              <p className="font-soft text-base mt-3 leading-relaxed whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{b.value}</p>
            </div>
          ))}
        </section>

        {/* Notes */}
        <section className="card-luxe">
          <p className="overline">Private notes</p>
          <textarea className="input-luxe mt-3" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Call notes, follow-up reminders, vibe check…" data-testid="lead-notes" />
          <div className="mt-4">
            <button onClick={saveNotes} className="btn-secondary" data-testid="lead-save-notes">Save notes</button>
          </div>
        </section>

        <section className="card-luxe text-sm" style={{ background: "var(--surface-2)" }}>
          <p className="overline">Referral · Other</p>
          <p className="font-soft text-base mt-3" style={{ color: "var(--text-secondary)" }}>
            <strong>How they found you:</strong> {active.referral || "—"}<br />
            <strong>Budget aware:</strong> {active.budget_aware ? "Yes" : "—"}<br />
            <strong>Submitted:</strong> {new Date(active.created_at).toLocaleString()}
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="coach-leads">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Inbox</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">
            Leads &amp; <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "var(--accent)", letterSpacing: 0 }}>applications</span>
          </h1>
          <div className="bronze-rule mt-6" />
        </div>
        <button onClick={exportCsv} className="btn-secondary" data-testid="export-leads-btn">
          <Download size={14} /> Export CSV
        </button>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {[{ v: "all", label: "All" }, ...STATUSES].map((s) => (
          <button key={s.v} onClick={() => setFilter(s.v)} data-testid={`lead-filter-${s.v}`}
            className="px-4 py-2 font-label text-[10px] tracking-[0.2em] uppercase border transition-colors"
            style={{
              borderColor: filter === s.v ? "var(--accent)" : "var(--border-strong)",
              background: filter === s.v ? "rgba(168,152,128,0.08)" : "transparent",
              color: filter === s.v ? "var(--accent)" : "var(--text-secondary)",
            }}>{s.label}</button>
        ))}
      </div>

      {/* Leads list */}
      <div className="border" style={{ borderColor: "var(--border-strong)" }}>
        {leads.length === 0 && (
          <div className="p-12 text-center">
            <Inbox size={32} className="mx-auto mb-4" style={{ color: "var(--text-tertiary)" }} />
            <p className="font-soft text-lg" style={{ color: "var(--text-secondary)" }}>No applications yet.</p>
          </div>
        )}
        {leads.map((l, i) => (
          <button key={l.lead_id} onClick={() => open(l)} data-testid={`lead-row-${l.lead_id}`}
            className="w-full text-left p-5 md:p-7 flex items-center justify-between gap-4 transition-colors hover:bg-surface-2"
            style={{ borderTop: i > 0 ? "0.5px solid var(--border)" : "none", background: "var(--surface-1)" }}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-display text-xl truncate">{l.name}</h3>
                <span className="font-label text-[10px] tracking-[0.2em] uppercase px-2 py-1 border" style={{ borderColor: l.status === "new" ? "var(--accent)" : "var(--border-strong)", color: l.status === "new" ? "var(--accent)" : "var(--text-tertiary)" }}>{l.status}</span>
              </div>
              <div className="font-label text-[11px] tracking-wider mt-2" style={{ color: "var(--text-secondary)" }}>
                {l.email}{l.instagram && ` · ${l.instagram}`}
              </div>
              <div className="font-soft text-base mt-3 line-clamp-1" style={{ color: "var(--text-secondary)" }}>
                {PROGRAM_LABEL[l.program_interest]} · Commit {l.commitment_level || "—"}/10 · {l.top_goal?.slice(0, 80) || "—"}
              </div>
            </div>
            <ChevronRight size={16} style={{ color: "var(--text-tertiary)" }} />
          </button>
        ))}
      </div>
    </div>
  );
}
